import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { LogOut, Camera as CameraIcon } from 'lucide-react';
import api, { clearAuth } from '../../api/Client';
import { fetchUserProfile } from '../../api/User';
import { SecureStorage } from '../../storage/SecureStorage';
import { API_BASE } from '../../config';
import styles from '../../styles/Move.module.css';

export default function MoveProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const user = await SecureStorage.getUser?.();
      if (!user) return;
      setUserId(user.id ?? null);
      setName(user.fullName ?? '');
      setEmail(user.email ?? '');
      const pic = user.profilePictureName ?? (user as any).profile_pic ?? null;
      if (pic) setPhotoUrl(`${API_BASE}/api/profile_pic/${pic}`);
    })();
  }, []);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }

  async function uploadPhoto(file: File) {
    if (!userId) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('user_id', String(userId));
      form.append('profile_pic', file);
      await api.put('/api/mobile/user', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const updated = await fetchUserProfile(userId);
      const pic = updated.profilePictureName ?? (updated as any).profile_pic ?? null;
      if (pic) setPhotoUrl(`${API_BASE}/api/profile_pic/${pic}?t=${Date.now()}`);
      flash('Foto actualizada');
    } catch {
      flash('No se pudo actualizar la foto');
    } finally {
      setUploading(false);
    }
  }

  async function pickPhoto() {
    if (Capacitor.getPlatform() === 'web') {
      fileInputRef.current?.click();
      return;
    }
    try {
      const image = await Camera.getPhoto({
        quality: 80,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
      });
      if (!image.dataUrl) return;
      const res = await fetch(image.dataUrl);
      const blob = await res.blob();
      await uploadPhoto(new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' }));
    } catch { /* user cancelled */ }
  }

  async function handleLogout() {
    await clearAuth();
    navigate('/home', { replace: true });
  }

  return (
    <div className={`move-app ${styles.page}`}>
      <header className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroOrb} />
        <div className={styles.heroInner}>
          <p className={styles.greetingLabel}>Tu cuenta</p>
          <h1 className={styles.greetingName}>Perfil</h1>
        </div>
      </header>

      <section className={styles.section} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '2rem' }}>
        {/* Avatar con botón de cámara */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <div
            onClick={pickPhoto}
            style={{
              width: 100, height: 100, borderRadius: '50%',
              overflow: 'hidden', border: '3px solid var(--move-primary)',
              background: 'var(--move-muted)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {photoUrl
              ? <img src={photoUrl} alt="perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 36, color: 'var(--move-muted-fg)' }}>
                  {name ? name[0].toUpperCase() : '?'}
                </span>
            }
          </div>
          <button
            onClick={pickPhoto}
            disabled={uploading}
            style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 32, height: 32, borderRadius: '50%', border: 0,
              background: 'var(--move-grad-primary)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: 'var(--move-shadow-glow)', cursor: 'pointer',
              opacity: uploading ? 0.6 : 1,
            }}
            aria-label="Cambiar foto"
          >
            <CameraIcon size={15} />
          </button>
        </div>

        <p style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: 'var(--move-fg)' }}>{name}</p>
        <p style={{ margin: '0 0 32px', fontSize: 14, color: 'var(--move-muted-fg)' }}>{email}</p>

        {/* Archivo oculto para web */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) uploadPhoto(file);
            e.target.value = '';
          }}
        />

        <button
          onClick={handleLogout}
          style={{
            width: '100%', maxWidth: 320,
            padding: '14px 0', border: 0, borderRadius: 14,
            background: 'hsl(0 72% 95%)', color: 'hsl(0 72% 40%)',
            fontSize: 15, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <LogOut size={18} />
          Cerrar sesión
        </button>
      </section>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 100, left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--move-secondary)', color: '#fff',
          padding: '10px 18px', borderRadius: 999,
          fontSize: 13, fontWeight: 600,
          boxShadow: 'var(--move-shadow-md)', zIndex: 100,
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
